/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, NgZone, OnChanges} from '@angular/core';
import {handleCopyModalResult} from '../common/util/handle-copy-modal-result';
import {ToastrService} from 'ngx-toastr';

import {AngularModalService} from '../common/modal/angular-modal.service';
import {wireModelCommsModelActions} from '../common/controller/model-comms-actions';
import {LineItemTemplateCopyModalComponent} from './line-item-template-copy-modal.component';
import template from './line-item-template-context-actions.component.html';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-line-item-template-context-actions',
  template
})
export class LineItemTemplateContextActionsComponent implements OnChanges {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;
  @Input() readOnly: boolean;
  @Input() sectionParam: string;

  readonly confirmation = {
    warningMessage: "The {{action}} of the Line Item Template will not affect existing line items created from it.",
    object: 'Line Item Template'
  };

  effectiveReadOnly = false;

  constructor(
    private readonly $state: StateService,
    private readonly toastr: ToastrService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly angularModalService: AngularModalService,
    private readonly ngZone: NgZone
  ) {
  }

  ngOnChanges(): void {
    this.resolveReadOnly();
    if (this.model && this.modelComms) {
      wireModelCommsModelActions(this.modelComms);
    }
  }

  createLineItem(): void {
    if (this.effectiveReadOnly || !this.model) {
      return;
    }
    this.openCopyModal(true);
  }

  copy(): void {
    if (this.effectiveReadOnly || !this.model) {
      return;
    }
    this.openCopyModal(false);
  }

  private openCopyModal(copyToLineItem: boolean): void {
    const modalRef = this.angularModalService.show(LineItemTemplateCopyModalComponent, {
      initialState: {
        model: this.model,
        copyToLineItem,
        fromLineItemList: false
      },
      class: 'modal-lg'
    });
    (modalRef.content as LineItemTemplateCopyModalComponent).onCopied.subscribe((result) => {
      handleCopyModalResult(() => {
        this.ngZone.run(() => {
          const url = copyToLineItem ? 'app.adv.line-items.line-item' : 'app.adv.line-item-templates.line-item-template';
          const text = copyToLineItem ? 'line item' : 'line item template';
          if (result.destination) {
            this.$state.go(url, {id: result.newId}, {inherit: false});
          } else {
            this.toastr.success('A copy of this ' + text + ' was successfully created.', text.toUpperCase() + ' Copied');
          }
        });
      });
    });
  }

  private resolveReadOnly(): void {
    const sectionPerms = this.LocalUserPermissions.getSectionPerms();
    const sectionParam = this.sectionParam;
    const specificEditableParameter = sectionParam + 'Editable';
    if (sectionParam && Object.prototype.hasOwnProperty.call(sectionPerms, specificEditableParameter)) {
      this.effectiveReadOnly = !sectionPerms[specificEditableParameter];
    } else if (sectionParam && Object.prototype.hasOwnProperty.call(sectionPerms, sectionParam)) {
      this.effectiveReadOnly = !sectionPerms[sectionParam];
    } else {
      this.effectiveReadOnly = !!this.readOnly;
    }
  }
}
